"use client";

// ProductDetailPage como ilha client. Recebe parts/fabricantes/t/lang/
// productSlug por props (do wrapper Server Component). Mantém estado
// local pra modal PDF + fetch do produto por id.

import { Fragment, useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { useSiteData } from "../lib/site-context";
import {
  buildProductSlug,
  displayRef,
  isAllCapsHeading,
  parseProductIdFromSlug,
} from "../lib/mbm-helpers";
import { withLocale } from "../lib/i18n";
import { useLangContext } from "../lib/lang-context";
import ProductPdfRequest from "./product-pdf-form";
import { BackLink } from "./mbm-components";

function IconFunnel() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 3h12l-4.5 6v4l-3 1V9z" />
    </svg>
  );
}

function SpecTable({ rows }) {
  const normalized = rows.map((r) =>
    Array.isArray(r) ? { key: r[0], val: r[1] }
    : typeof r === "string" ? { key: null, val: r }
    : r
  );
  return (
    <ul className="pd-spec-table">
      {normalized.map((row, i) => (
        <li key={i}>
          <span className="pd-spec-idx">[{String(i + 1).padStart(2, "0")}]</span>
          {row.key ? (
            <>
              <span className="pd-spec-key">{row.key}</span>
              <span className="pd-spec-val">{row.val}</span>
            </>
          ) : (
            <span className="pd-spec-val" style={{ gridColumn: "span 2" }}>{row.val}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function CompatChips({ brands, fabricantes, lang }) {
  const norm = (s) => String(s).toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  const findSlug = (name) => {
    const target = norm(name);
    return fabricantes.find((f) => norm(f.nome) === target)?.slug || null;
  };
  return (
    <div className="pd-compat">
      <span className="pd-compat-label">Compatível com</span>
      {brands.map((b) => {
        const slug = findSlug(b);
        if (slug) {
          return (
            <Link
              key={b}
              href={withLocale(`/pecas?fabricante=${slug}`, lang)}
              className="pd-chip"
              title={`Ver peças compatíveis com ${b}`}
            >
              <IconFunnel />
              {b}
            </Link>
          );
        }
        return (
          <span key={b} className="pd-chip" title={b}>
            <IconFunnel />
            {b}
          </span>
        );
      })}
    </div>
  );
}

function ProcessFlowHorizontal({ process, processSteps }) {
  const steps = String(process || "")
    .split(/\s*(?:→|->|>|\/)\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (steps.length === 0) return null;
  return (
    <ol className="pd-flow-h">
      {steps.map((label, i) => {
        const desc = (processSteps && processSteps[label.toLowerCase()]) || "—";
        return (
          <li key={i}>
            <span className="pd-flow-num">{String(i + 1).padStart(2, "0")}</span>
            <span className="pd-flow-label">{label}</span>
            <span className="pd-flow-desc">{desc}</span>
          </li>
        );
      })}
    </ol>
  );
}

function RelatedThumbSvg({ type }) {
  return (
    <svg viewBox="0 0 200 140" preserveAspectRatio="xMidYMid meet">
      <g stroke="#2A2E33" strokeWidth="0.6" fill="none" opacity="0.55">
        {type === "shaft" && (<>
          <rect x="40" y="60" width="120" height="20" />
          <circle cx="50" cy="70" r="8" />
          <circle cx="150" cy="70" r="8" />
        </>)}
        {type === "rod" && (<>
          <circle cx="50" cy="70" r="14" />
          <circle cx="150" cy="70" r="20" />
          <path d="M50,62 L150,52 M50,78 L150,90" />
        </>)}
        {type === "bushing" && (<>
          <circle cx="100" cy="70" r="40" />
          <circle cx="100" cy="70" r="20" />
        </>)}
        {type === "disc" && (<>
          <circle cx="100" cy="70" r="48" />
          <circle cx="100" cy="70" r="28" />
        </>)}
        {type === "gear" && (<>
          <circle cx="100" cy="70" r="42" />
          <circle cx="100" cy="70" r="12" />
        </>)}
        {type === "structure" && (<>
          <rect x="40" y="30" width="120" height="80" />
          <line x1="40" y1="50" x2="160" y2="50" />
        </>)}
        {type === "brake" && (<>
          <circle cx="100" cy="70" r="40" />
          <rect x="60" y="60" width="80" height="20" />
        </>)}
        {type === "rupture" && (<>
          <path d="M70,30 L130,30 L120,110 L80,110 Z" />
        </>)}
        {type === "screw" && (<>
          <rect x="50" y="65" width="100" height="10" />
          {Array.from({ length: 10 }).map((_, i) => (
            <line key={i} x1={50 + i * 10} y1="60" x2={50 + i * 10} y2="80" />
          ))}
        </>)}
        {type === "hammer" && (<>
          <rect x="80" y="20" width="40" height="40" />
          <rect x="98" y="60" width="4" height="60" />
        </>)}
        {type === "pump" && (<>
          <rect x="40" y="50" width="120" height="60" />
          <circle cx="100" cy="80" r="20" />
        </>)}
      </g>
    </svg>
  );
}

function ProductRelatedStrip({ product, parts, lang }) {
  const list = Array.isArray(parts) ? parts : [];
  const related = list
    .filter((p) => p.id !== product.id && (
      p.categoriaSlug === product.categoriaSlug ||
      (product.compatibility || []).some((b) => (p.compatibility || []).includes(b))
    ))
    .slice(0, 4);
  if (related.length === 0) return null;

  const catLabel = product.category?.[0] || "Catálogo";

  return (
    <section className="pd-related">
      <div className="sec-head">
        <div className="num">Relacionadas · {catLabel}</div>
        <h2 className="title">Peças que costumam vir juntas.</h2>
      </div>
      <div className="pd-related-grid">
        {related.map((p) => {
          const targetSlug = buildProductSlug(p);
          const photoUrl = p.imagemPrincipalUrl
            || (Array.isArray(p.fotos) ? p.fotos.find((f) => f?.url)?.url : null)
            || null;
          return (
            <Link key={p.id ?? p.ref}
                  href={withLocale(`/produto/${targetSlug}`, lang)}
                  className="pd-related-card">
              <div className={`pd-related-thumb ${photoUrl ? "pd-related-thumb-photo" : ""}`}>
                {photoUrl ? (
                  // Medido: 155.5 CSS px @375 (2 col), 352 @768, 220 @1100
                  // (4 col), 305 @1440, 333 acima (shell trava em 1440).
                  <Image
                    src={photoUrl}
                    alt={p.name?.[lang] ?? p.name?.pt ?? ""}
                    fill
                    sizes="(max-width: 768px) 46vw, (max-width: 1440px) 22vw, 340px"
                  />
                ) : (
                  <RelatedThumbSvg type={p.type} />
                )}
                {displayRef(p.ref) && <div className="pd-related-ref">{displayRef(p.ref)}</div>}
              </div>
              <div className="pd-related-body">
                <h3>{p.name?.[lang] ?? p.name?.pt ?? ""}</h3>
                <div className="pd-related-meta">{(p.category || []).join(" · ")}</div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function ProductDescFormatted({ source }) {
  const text = String(source ?? "").trim();
  if (!text) return null;
  const blocks = text.split(/\n\s*\n+/).map((b) => b.trim()).filter(Boolean);
  return (
    <div className="pd-desc">
      {blocks.map((block, i) => {
        const lines = block.split(/\n/).map((l) => l.trim()).filter(Boolean);
        if (lines.length === 1 && isAllCapsHeading(lines[0])) {
          return <h3 key={i} className="pd-desc-heading">{lines[0]}</h3>;
        }
        return (
          <p key={i} className="pd-desc-p">
            {lines.map((ln, j) => (
              <Fragment key={j}>
                {ln}
                {j < lines.length - 1 && <br />}
              </Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}

export default function ProductDetailPage({
  parts,
  fabricantes,
  lang,
  productSlug,
}) {
  const { t } = useLangContext();
  const { processSteps } = useSiteData();
  const productId = parseProductIdFromSlug(productSlug);

  const [pdfModalOpen, setPdfModalOpen] = useState(false);

  const [remote, setRemote] = useState({
    id: null,
    product: null,
    errored: false,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [productSlug]);

  useEffect(() => {
    if (!productId) return undefined;
    let cancelled = false;
    fetch(`/api/products/${productId}?lang=${lang}`)
      .then(async (r) => {
        if (!r.ok) {
          throw new Error(`HTTP ${r.status}`);
        }
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        setRemote({ id: productId, product: data, errored: false });
      })
      .catch(() => {
        if (cancelled) return;
        setRemote({ id: productId, product: null, errored: true });
      });
    return () => {
      cancelled = true;
    };
  }, [productId, lang]);

  if (productId == null) {
    return (
      <div className="page">
        <section className="hero shell">
          <h1 style={{ fontSize: "clamp(40px, 6vw, 72px)" }}>
            {t("product_not_found")}
          </h1>
          <div style={{ marginTop: 24, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Link className="cta-btn" href={withLocale("/pecas", lang)}>
              {t("back_to_catalog")} <span className="arr" aria-hidden="true">→</span>
            </Link>
          </div>
        </section>
      </div>
    );
  }

  const isStale = remote.id !== productId;
  const loading = isStale && !remote.errored;
  const product = !isStale && !remote.errored ? remote.product : null;
  const notFound = !isStale && remote.errored;

  if (loading) {
    return (
      <div className="page">
        <section className="hero shell">
          <div className="label-num">{t("loading")}</div>
        </section>
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="page">
        <section className="hero shell">
          <div className="meta-row">
            <BackLink
              href={withLocale("/pecas", lang)}
              kind="catalog"
              lang={lang}
              label={t("back_to_catalog")}
            />
          </div>
          <h1 style={{ fontSize: "clamp(40px, 6vw, 72px)" }}>
            {t("product_not_found")}
          </h1>
          <p className="sub" style={{ maxWidth: "60ch" }}>
            {t("product_not_found_message")}
          </p>
          <div style={{ marginTop: 24, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Link className="cta-btn" href={withLocale("/pecas", lang)}>
              {t("back_to_catalog")} <span className="arr" aria-hidden="true">→</span>
            </Link>
          </div>
        </section>
      </div>
    );
  }

  const productName = product.name?.[lang] ?? product.name?.pt ?? "";
  const productDesc = product.desc?.[lang] ?? "";
  const photoUrl = product.imagemPrincipalUrl
    || (Array.isArray(product.fotos) ? product.fotos.find((f) => f?.url)?.url : null)
    || null;
  // `product.fotos` já vinha adaptado em lib/site-data.js e nunca era
  // renderizado — o produto mostrava uma foto só enquanto /maquinas tinha
  // galeria. Aqui entram as restantes (a principal já está no frame acima).
  const galleryFotos = (Array.isArray(product.fotos) ? product.fotos : [])
    .filter((f) => f?.url && f.url !== photoUrl);

  return (
    <article className="page pd-page">
      <header className="pd-hero shell">
        <div className="meta-row">
          <BackLink
            href={withLocale("/pecas", lang)}
            kind="catalog"
            lang={lang}
            label={t("back_to_catalog")}
          />
          <span>{[displayRef(product.ref), ...(product.category ?? [])].filter(Boolean).join(" · ")}</span>
        </div>
      </header>

      <section className="pd-body pd-grid-photo shell">
        <div className="pd-photo-col">
          {photoUrl ? (
            // `.pd-photo-col` vira `position: static` em ≤1024px, então
            // `fill` precisa de um frame relativo próprio.
            // Medido: 327 CSS px @375, 720 @768, 413 @1100, 575 @1440, 629 acima.
            <div className="pd-photo media-frame">
              <Image
                className="media-fit-contain"
                src={photoUrl}
                alt={productName}
                fill
                loading="eager"
                sizes="(max-width: 768px) 94vw, (max-width: 1440px) 40vw, 640px"
              />
            </div>
          ) : (
            <div className="pd-photo pd-photo-empty" aria-hidden="true" />
          )}

          {galleryFotos.length > 0 && (
            <div className="product-gallery">
              {galleryFotos.map((f, i) => (
                <a
                  key={f.id ?? f.url}
                  className="product-gallery-item media-frame"
                  href={f.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {/* Medido: 196 CSS px @375 (tira snap-scroll), 281 @1440
                      (grid de 2 col dentro da coluna da foto). */}
                  <Image
                    src={f.url}
                    alt={`${productName} — foto ${i + 2}`}
                    fill
                    sizes="(max-width: 768px) 62vw, (max-width: 1440px) 20vw, 310px"
                  />
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="pd-content-col">
          <h1 className="pd-h1">{productName}</h1>

          {productDesc && <ProductDescFormatted source={productDesc} />}

          {product.specs?.length > 0 && (
            <div className="pd-specs-wrap">
              <div className="label-num">{t("specs_label")}</div>
              <SpecTable rows={product.specs} />
            </div>
          )}

          {product.compatibility?.length > 0 && (
            <div className="pd-compat-wrap">
              <CompatChips
                brands={product.compatibility}
                fabricantes={Array.isArray(fabricantes) ? fabricantes : []}
                lang={lang}
              />
            </div>
          )}

          {product.process && (
            <div className="pd-process-wrap">
              <div className="label-num" style={{ marginBottom: 12 }}>
                {t("process_label")}
              </div>
              <ProcessFlowHorizontal process={product.process} processSteps={processSteps} />
            </div>
          )}

          <div className="pd-actions">
            <button
              type="button"
              className="cta-btn"
              onClick={() => setPdfModalOpen(true)}
            >
              {t("download_pdf") || "Baixar PDF"} <span className="arr" aria-hidden="true">↓</span>
            </button>
          </div>
        </div>
      </section>

      <div className="shell">
        <ProductRelatedStrip
          product={product}
          parts={parts}
          lang={lang}
        />
      </div>

      <ProductPdfRequest
        productId={product.id}
        productName={productName}
        open={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
      />
    </article>
  );
}
